/**
 * ScenarioSelectPage — Training Portal entry point.
 * Redesigned into a premium 2-step wizard.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './ScenarioSelectPage.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Scenario {
  id: string;
  title: string;
  description: string;
  difficulty: 'easy' | 'intermediate' | 'advanced';
  language: string;
  product_type: string;
  min_turns?: number;
  max_turns?: number;
  conversation_starter?: 'agent' | 'ai';
}

/** Rough session-length estimate (minutes) from the scenario's turn budget. */
function estimateMinutes(scenario: Scenario): number {
  const turns = scenario.max_turns ?? 12;
  return Math.max(5, Math.round(turns * 0.75));
}

/** "life_insurance" → "Life Insurance" */
function formatProductType(value: string): string {
  return value
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

type Language = 'en' | 'zh-TW';

export interface Persona {
  id: string;
  name: string;
  description: string | null;
  characteristics: string | null;
  language: string;
  market: string;
}

export function getI18nKey(id: string): string {
  if (id === 'skeptical-business-owner') return 'skeptical-professional';
  if (id === 'busy-working-parent') return 'busy-parent';
  return id;
}

type ApiError = 'SESSION_ACTIVE' | 'PIPELINE_UNAVAILABLE' | 'INVALID_SCENARIO' | 'UNAUTHORIZED' | null;

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchScenarios(token: string): Promise<Scenario[]> {
  const res = await fetch('/api/v1/scenarios?country=TW&is_active=true', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  const body = await res.json();
  return body.data.scenarios as Scenario[];
}

async function fetchPersonas(token: string): Promise<Persona[]> {
  const res = await fetch('/api/v1/personas', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  const body = await res.json();
  return body.data as Persona[];
}

async function startSession(
  token: string,
  scenarioId: string,
  language: Language,
  personaId: string,
  sessionLengthMinutes: number,
  customerMood: string,
  evaluationMode: 'coaching' | 'assessment',
): Promise<{ session_id: string; ws_url: string; reconnect_token: string }> {
  const res = await fetch('/api/v1/sessions', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      scenario_id: scenarioId,
      language,
      persona_id: personaId,
      session_length_minutes: sessionLengthMinutes,
      customer_mood: customerMood,
      evaluation_mode: evaluationMode,
    }),
  });

  const body = await res.json();

  if (res.status === 409) throw Object.assign(new Error('SESSION_ACTIVE'), { data: body.detail });
  if (res.status === 400) throw Object.assign(new Error('INVALID_SCENARIO'), { data: body.detail });
  if (res.status === 503) throw new Error('PIPELINE_UNAVAILABLE');
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error('UNKNOWN_ERROR');

  return body.data;
}

async function endExistingSession(token: string, sessionId: string): Promise<void> {
  await fetch(`/api/v1/sessions/${sessionId}/end`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ScenarioSelectPage(): JSX.Element {
  const { t } = useTranslation('training');
  const navigate = useNavigate();

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  
  // Wizard state: 1 = Select Scenario, 2 = Configure Session
  const [step, setStep] = useState<1 | 2>(1);

  // Settings state
  const [language, setLanguage] = useState<Language>('en');
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>('first-time-buyer');
  const [customerMood, setCustomerMood] = useState<string>('Skeptical');
  const [difficulty, setDifficulty] = useState<'easy' | 'intermediate' | 'advanced'>('intermediate');
  const [sessionLength, setSessionLength] = useState<number>(10);
  const [evaluationMode, setEvaluationMode] = useState<'coaching' | 'assessment'>(
    sessionStorage.getItem('module_id') ? 'assessment' : 'coaching',
  );
  const [conversationStarter, setConversationStarter] = useState<'user' | 'ai'>('user');

  const [apiError, setApiError] = useState<ApiError>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'easy' | 'intermediate' | 'advanced'>('all');

  const token = sessionStorage.getItem('access_token') ?? '';

  // Load scenarios and personas on mount
  useEffect(() => {
    let cancelled = false;

    async function loadData(): Promise<void> {
      setIsLoading(true);
      try {
        const [scenariosData, personasData] = await Promise.all([
          fetchScenarios(token),
          fetchPersonas(token),
        ]);
        if (!cancelled) {
          setScenarios(scenariosData);
          setPersonas(personasData);
          if (personasData.length > 0) {
            const defaultPersona = personasData.find(p => p.id === 'first-time-buyer') || personasData[0];
            setSelectedPersonaId(defaultPersona.id);
          }
        }
      } catch (err) {
        if (err instanceof Error && err.message === 'UNAUTHORIZED') {
          sessionStorage.clear();
          navigate('/login');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void loadData();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  // Default the conversation opener from the selected scenario's admin setting
  // (per-scenario, saved to DB). The trainee can still override below.
  useEffect(() => {
    if (selectedScenario?.conversation_starter) {
      setConversationStarter(selectedScenario.conversation_starter === 'ai' ? 'ai' : 'user');
    }
  }, [selectedScenario]);

  // Submit — start session
  async function handleStartSession(): Promise<void> {
    if (!selectedScenario || isSubmitting) return;
    setApiError(null);
    setIsSubmitting(true);

    try {
      const data = await startSession(
        token,
        selectedScenario.id,
        language,
        selectedPersonaId,
        sessionLength,
        customerMood,
        evaluationMode,
      );

      sessionStorage.setItem('ws_url', data.ws_url);
      sessionStorage.setItem('session_id', data.session_id);
      sessionStorage.setItem('scenario_id', selectedScenario.id);
      sessionStorage.setItem('scenario_title', selectedScenario.title);
      sessionStorage.setItem('session_language', language);
      sessionStorage.setItem('persona_id', selectedPersonaId);
      sessionStorage.setItem('customer_mood', customerMood);
      sessionStorage.setItem('difficulty', difficulty);
      sessionStorage.setItem('session_length', String(sessionLength));
      sessionStorage.setItem('evaluation_mode', evaluationMode);
      sessionStorage.setItem('conversation_starter', conversationStarter);

      navigate(`/session/${data.session_id}`);
    } catch (err) {
      if (err instanceof Error) {
        const code = err.message as ApiError;
        setApiError(code);

        if (code === 'SESSION_ACTIVE') {
          const anyErr = err as Error & { data?: { session_id?: string } };
          setActiveSessionId(anyErr.data?.session_id ?? null);
        }

        if (code === 'UNAUTHORIZED') {
          sessionStorage.clear();
          navigate('/login');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // Active session conflict modal handlers
  function handleResumeSession(): void {
    if (activeSessionId) {
      navigate(`/session/${activeSessionId}`);
    }
  }

  async function handleEndAndRestart(): Promise<void> {
    if (activeSessionId) {
      await endExistingSession(token, activeSessionId);
      setActiveSessionId(null);
      setApiError(null);
      void handleStartSession();
    }
  }

  // Filters and search
  const filteredScenarios = scenarios.filter((scenario) => {
    const matchesSearch = scenario.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          scenario.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDifficulty = selectedFilter === 'all' || scenario.difficulty === selectedFilter;
    return matchesSearch && matchesDifficulty;
  });

  const currentPersona = personas.find(p => p.id === selectedPersonaId) || personas[0] || {
    id: 'first-time-buyer',
    name: 'First-Time Buyer',
    description: 'Young professional looking to buy their first life/health policy.',
  };

  return (
    <main className={styles.page}>
      {/* Step 1: Select Training Scenario */}
      {step === 1 && (
        <div className={styles.stepContainer}>
          <div className={styles.header}>
            <div>
              <h1 className={styles.title}>Select Training Scenario</h1>
              <p className={styles.subtitle}>Choose a scenario to start your AI roleplay session.</p>
            </div>
            <button className={styles.filtersBtn}>
              ⚙ Filters
            </button>
          </div>

          <div className={styles.searchBarRow}>
            <input
              type="text"
              className={styles.searchBar}
              placeholder="Search scenarios..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className={styles.tabRow}>
            <button
              className={`${styles.tab} ${selectedFilter === 'all' ? styles.tabActive : ''}`}
              onClick={() => setSelectedFilter('all')}
            >
              All Scenarios ({scenarios.length})
            </button>
            <button
              className={`${styles.tab} ${selectedFilter === 'easy' ? styles.tabActive : ''}`}
              onClick={() => setSelectedFilter('easy')}
            >
              Beginner
            </button>
            <button
              className={`${styles.tab} ${selectedFilter === 'intermediate' ? styles.tabActive : ''}`}
              onClick={() => setSelectedFilter('intermediate')}
            >
              Intermediate
            </button>
            <button
              className={`${styles.tab} ${selectedFilter === 'advanced' ? styles.tabActive : ''}`}
              onClick={() => setSelectedFilter('advanced')}
            >
              Advanced
            </button>
          </div>

          {isLoading ? (
            <p className={styles.loading}>{t('scenarios.loading')}</p>
          ) : filteredScenarios.length === 0 ? (
            <p className={styles.emptyState}>{t('scenarios.emptyState')}</p>
          ) : (
            <div className={styles.scenarioGrid}>
              {filteredScenarios.map((scenario) => {
                const isSelected = selectedScenario?.id === scenario.id;
                return (
                  <div
                    key={scenario.id}
                    className={`${styles.scenarioCard} ${isSelected ? styles.scenarioCardSelected : ''}`}
                    onClick={() => {
                      setSelectedScenario(scenario);
                      setDifficulty(scenario.difficulty);
                    }}
                  >
                    <div className={styles.cardHeader}>
                      <span className={styles.scenarioTitle}>{scenario.title}</span>
                      <span className={`${styles.difficultyBadge} ${styles[`difficulty_${scenario.difficulty}`]}`}>
                        {scenario.difficulty.toUpperCase()}
                      </span>
                    </div>
                    <p className={styles.scenarioDescription}>{scenario.description}</p>
                    <div className={styles.cardFooter}>
                      <span className={styles.metaTag}>⏱ ~{estimateMinutes(scenario)} min</span>
                      <span className={styles.metaTag}>{formatProductType(scenario.product_type)}</span>
                      <button
                        className={styles.selectBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedScenario(scenario);
                          setDifficulty(scenario.difficulty);
                          setStep(2);
                        }}
                      >
                        Select
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Configure Your Session */}
      {step === 2 && selectedScenario && (
        <div className={styles.stepContainer}>
          <button className={styles.backBtn} onClick={() => setStep(1)}>
            ← Back
          </button>
          
          <h1 className={styles.title}>Configure Your Session</h1>
          <p className={styles.subtitle}>Review your selections and customize the session.</p>

          <div className={styles.configGrid}>
            {/* Left: Your Selections */}
            <div className={styles.configColumnLeft}>
              <h2 className={styles.columnTitle}>Your selections</h2>
              
              <div className={styles.selectionCard}>
                <span className={styles.selectionLabel}>Scenario</span>
                <h3 className={styles.selectionTitle}>{selectedScenario.title}</h3>
                <p className={styles.selectionDesc}>{selectedScenario.description}</p>
                <div className={styles.selectionTags}>
                  <span className={`${styles.difficultyBadge} ${styles[`difficulty_${selectedScenario.difficulty}`]}`}>
                    {selectedScenario.difficulty.toUpperCase()}
                  </span>
                  <span className={styles.metaTag}>⏱ ~{estimateMinutes(selectedScenario)} min</span>
                </div>
              </div>

              <div className={styles.selectionCard}>
                <span className={styles.selectionLabel}>Persona</span>
                <h3 className={styles.selectionTitle}>
                  {t(`scenarios.personas.${getI18nKey(currentPersona.id)}.name`, currentPersona.name)}
                </h3>
                <p className={styles.selectionDesc}>
                  {t(`scenarios.personas.${getI18nKey(currentPersona.id)}.description`, currentPersona.description || 'AI Customer Roleplay Character.')}
                </p>
                <div className={styles.personaSelectorGrid}>
                  {personas.map((persona) => (
                    <button
                      key={persona.id}
                      type="button"
                      aria-pressed={selectedPersonaId === persona.id ? 'true' : 'false'}
                      className={`${styles.personaSelectorBtn} ${selectedPersonaId === persona.id ? styles.personaSelectorBtnActive : ''}`}
                      onClick={() => setSelectedPersonaId(persona.id)}
                    >
                      {t(
                        `scenarios.personas.${getI18nKey(persona.id)}.name`,
                        persona.name,
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Session Settings */}
            <div className={styles.configColumnRight}>
              <h2 className={styles.columnTitle}>Session Settings</h2>

              <div className={styles.fieldGroup}>
                <label htmlFor="config-language" className={styles.fieldLabel}>Language</label>
                <select
                  id="config-language"
                  title="Language"
                  className={styles.selectInput}
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as Language)}
                >
                  <option value="en">English (US)</option>
                  <option value="zh-TW">Traditional Chinese</option>
                </select>
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="config-customer-mood" className={styles.fieldLabel}>Customer Mood</label>
                <select
                  id="config-customer-mood"
                  title="Customer Mood"
                  className={styles.selectInput}
                  value={customerMood}
                  onChange={(e) => setCustomerMood(e.target.value)}
                >
                  <option value="Skeptical">Skeptical</option>
                  <option value="Friendly">Friendly</option>
                  <option value="Demanding">Demanding</option>
                  <option value="Indifferent">Indifferent</option>
                </select>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Difficulty Level</label>
                <div className={styles.badgeRow}>
                  {(['easy', 'intermediate', 'advanced'] as const).map((diff) => (
                    <button
                      key={diff}
                      type="button"
                      className={`${styles.badgeBtn} ${difficulty === diff ? styles.badgeBtnActive : ''}`}
                      onClick={() => setDifficulty(diff)}
                    >
                      {diff.charAt(0).toUpperCase() + diff.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Session Length</label>
                <div className={styles.badgeRow}>
                  {[5, 10, 15].map((len) => (
                    <button
                      key={len}
                      type="button"
                      className={`${styles.badgeBtn} ${sessionLength === len ? styles.badgeBtnActive : ''}`}
                      onClick={() => setSessionLength(len)}
                    >
                      {len} Min
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Evaluation Mode</label>
                <div className={styles.badgeRow}>
                  {(['coaching', 'assessment'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={`${styles.badgeBtn} ${evaluationMode === mode ? styles.badgeBtnActive : ''}`}
                      onClick={() => setEvaluationMode(mode)}
                    >
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Who starts the conversation</label>
                <div className={styles.badgeRow}>
                  {([['user', 'I start'], ['ai', 'AI starts']] as const).map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      className={`${styles.badgeBtn} ${conversationStarter === val ? styles.badgeBtnActive : ''}`}
                      onClick={() => setConversationStarter(val)}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.evaluationFooter}>
            <h3 className={styles.evaluationTitle}>AI will evaluate you on</h3>
            <div className={styles.evalTagRow}>
              <span className={styles.evalTag} style={{ background: '#e3f2fd', color: '#0d47a1' }}>Rapport Building</span>
              <span className={styles.evalTag} style={{ background: '#e8f5e9', color: '#1b5e20' }}>Needs Discovery</span>
              <span className={styles.evalTag} style={{ background: '#ffebee', color: '#b71c1c' }}>Objection Handling</span>
              <span className={styles.evalTag} style={{ background: '#fff8e1', color: '#f57f17' }}>Product Knowledge</span>
              <span className={styles.evalTag} style={{ background: '#f3e5f5', color: '#4a148c' }}>Closing Techniques</span>
            </div>
          </div>

          <button
            className={styles.startBtn}
            onClick={() => void handleStartSession()}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('scenarios.starting', 'Starting Session…') : t('scenarios.startButton', 'Start AI Roleplay Session')}
          </button>
        </div>
      )}

      {/* SESSION_ACTIVE modal */}
      {apiError === 'SESSION_ACTIVE' && (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="session-active-title">
          <div className={styles.modal}>
            <div className={styles.modalIcon} aria-hidden="true">⏱</div>
            <h3 id="session-active-title" className={styles.modalTitle}>
              {t('scenarios.errors.sessionActiveTitle', 'Active session in progress')}
            </h3>
            <p className={styles.modalText}>{t('scenarios.errors.sessionActive')}</p>
            <div className={styles.modalActions}>
              <button className={styles.btnPrimary} onClick={handleResumeSession}>
                {t('scenarios.errors.sessionActiveResume')}
              </button>
              <button className={styles.btnSecondary} onClick={() => void handleEndAndRestart()}>
                {t('scenarios.errors.sessionActiveEnd')}
              </button>
            </div>
          </div>
        </div>
      )}

      {apiError === 'PIPELINE_UNAVAILABLE' && (
        <div className={styles.errorBanner} role="alert">
          {t('scenarios.errors.pipelineUnavailable', 'AI speech pipeline is currently unavailable. Please try again.')}
        </div>
      )}

      {apiError === 'INVALID_SCENARIO' && (
        <div className={styles.errorBanner} role="alert">
          {t('scenarios.errors.invalidScenario', 'Selected scenario is invalid or inactive.')}
        </div>
      )}
    </main>
  );
}
